// CSS-as-string for iframe injection — see StoryContentsFrame/story.tsx for context.
// eslint-disable-next-line import/no-unresolved
import storyContentStyles from '@opengov/stories-shared/styles/story-modules.css?inline';
import { CircularProgress, Typography, Box, Paper } from '@mui/material';
import { applyCustomCssToScope, getStoryCustomCssScopeId } from '@opengov/stories-shared/utils';
import React, { useEffect, useState } from 'react';
import Frame, { FrameContextComponent } from 'react-frame-component';
import { useQueryClient } from '@tanstack/react-query';

import {
  HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME,
  PUBLICATION_CUSTOM_CSS_ID_ATTR,
  STORY_CUSTOM_CSS_ID_ATTR
} from '../../../../utils/stylesheetsUtils';
import { FolderSvg } from '../../../StoryList/StoryListContent/FolderSvg';

import { fetchSectionContent, updateSectionContent } from '../api/sectionContent';
import { useStoryHtmlContent } from '../hooks/useStoryHtmlContent';
import { publicationSetupPdfQueryKeys } from '../hooks/publicationSetupPdfQueryKeys';
import { styles } from './StoryPreview.styles';
import { ISetupPdfMessages } from '../../../../../types/setupPDFTypes';
import { useProgressTrackerSettingsContext } from '../../../StoriesSettings/ProgressTrackerSettings/context/useProgressTrackerSettingsContext';

const storyContentIdPrefix = 'storyContent-';
const pageBreakClassName = 'setup-pdf-page-break';
const HEADER_FOOTER_ZOOM_FACTOR = 0.52;
const SECTION_ZOOM_FACTOR = 0.75;
const SECTION_PADDING_FACTOR = '1in';
export type IStoryPreviewProps = {
  activeElement: HTMLElement | null;
  iframeDocumentRef: React.MutableRefObject<Document | null>;
  messages: ISetupPdfMessages;
  publicationCss: string;
  selectedStoryId: string | null;
  setActiveElement(activeElement: HTMLElement | null): void;
};

export const StoryPreview = (props: IStoryPreviewProps) => {
  const { activeElement, iframeDocumentRef, messages, publicationCss, selectedStoryId } = props;
  const [iframeDoc, setIframeDoc] = useState<Document | null>(null);
  const queryClient = useQueryClient();
  const { progressTrackerSettings } = useProgressTrackerSettingsContext();
  const storyHtmlQuery = useStoryHtmlContent(
    selectedStoryId ? { storyId: selectedStoryId, progressTrackerSettings } : null
  );
  const iframeContent = storyHtmlQuery.data?.html ?? null;
  const storyCustomCss = storyHtmlQuery.data?.storyCustomCss ?? '';
  const isLoadingContent = storyHtmlQuery.isLoading;
  const toggleSectionPageBreakClass = (htmlString: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const section = doc.querySelector('section');
    if (section) {
      section.classList.toggle(pageBreakClassName);
    }
    return doc.body.innerHTML;
  };

  /**
   * Adds toolbar buttons to each <section>
   */
  const addSectionButtons = (doc: Document) => {
    const sections = doc.querySelectorAll('section');

    sections.forEach((section) => {
      if (section.querySelector('.section-toolbar')) return; // skip duplicates

      const btn = doc.createElement('button');
      const hasBreak = section.classList.contains(pageBreakClassName);

      btn.className = 'section-toolbar ' + (hasBreak ? 'remove' : 'add');

      // Wrap label in span so border styling works
      const label = doc.createElement('span');
      label.className = 'setupPdfPageBreakButton';
      label.textContent = hasBreak ? messages.removePageBreak : messages.addPageBreak;
      btn.appendChild(label);

      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        e.preventDefault();

        const setupPdfPageBreakButton = btn.querySelector('.setupPdfPageBreakButton');
        if (!setupPdfPageBreakButton) return;

        const sectionIdWithPrefix = section.id;
        if (sectionIdWithPrefix.startsWith(storyContentIdPrefix) && selectedStoryId) {
          const sectionContentData = await fetchSectionContent({
            storyId: selectedStoryId,
            contentSubstring: `%${sectionIdWithPrefix}%`
          });
          const updatedSectionHtml = toggleSectionPageBreakClass(sectionContentData.content);
          await updateSectionContent({
            sectionId: sectionContentData.id,
            updatedContent: updatedSectionHtml
          });
          // Bust the storyHtml cache so switching back from preview to edit mode
          // re-fetches fresh HTML with the updated page-break class instead of
          // replaying the stale cached content.
          void queryClient.invalidateQueries({
            queryKey: publicationSetupPdfQueryKeys.storyHtml(selectedStoryId)
          });
        }

        if (section.classList.contains(pageBreakClassName)) {
          section.classList.remove(pageBreakClassName);
          btn.className = 'section-toolbar add';
          setupPdfPageBreakButton.textContent = messages.addPageBreak;
        } else {
          section.classList.add(pageBreakClassName);
          btn.className = 'section-toolbar remove';
          setupPdfPageBreakButton.textContent = messages.removePageBreak;
        }
      });

      section.insertBefore(btn, section.firstChild);
    });
  };

  const handleElementClick = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const subscribeIframeEvents = (ref: FrameContextComponent) => {
    //added type cast to fix type error
    const iframeDocument = (ref?.node as HTMLIFrameElement | null)?.contentWindow?.document;
    if (iframeDocument && !iframeDocumentRef.current) {
      iframeDocumentRef.current = iframeDocument;
      setIframeDoc(iframeDocument);
    }
  };

  useEffect(() => {
    iframeDocumentRef.current = null;
  }, [selectedStoryId, iframeDocumentRef]);

  useEffect(() => {
    if (activeElement) activeElement.classList.add(HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME);
    return () => {
      if (activeElement) activeElement.classList.remove(HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME);
    };
  }, [activeElement]);

  useEffect(() => {
    if (!iframeDoc) return;
    iframeDoc.body.addEventListener('click', handleElementClick);

    return () => {
      iframeDoc.body.removeEventListener('click', handleElementClick);
    };
  }, [iframeDoc]);

  if (!selectedStoryId) {
    return (
      <Box data-test="setupPDFStoryEmptyPreview" sx={styles.container}>
        <FolderSvg />
        <Typography color="text.secondary">{messages.selectStoryToPreview}</Typography>
      </Box>
    );
  }

  if (isLoadingContent) {
    return (
      <Box data-test="setupPDFStoryPreviewLoadingSpinner" sx={styles.container}>
        <CircularProgress />
        <Typography>{messages.loading}</Typography>
      </Box>
    );
  }

  const scopeId = getStoryCustomCssScopeId();
  const scopeSelector = `#${scopeId}`;
  return (
    <Paper data-test="setupPDFStoryPreview" sx={styles.container}>
      <Frame
        key={selectedStoryId}
        style={{
          width: '100%',
          height: '100%',
          border: 'none'
        }}
        ref={(ref: FrameContextComponent) => {
          setTimeout(() => subscribeIframeEvents(ref), 0);
        }}
        head={
          <>
            <style>{storyContentStyles}</style>
            <style data-id={PUBLICATION_CUSTOM_CSS_ID_ATTR}>
              {applyCustomCssToScope(publicationCss, scopeSelector)}
            </style>
            <style data-id={STORY_CUSTOM_CSS_ID_ATTR}>{applyCustomCssToScope(storyCustomCss, scopeSelector)}</style>
          </>
        }
        initialContent={iframeContent || ''}
        onLoad={() => {
          if (!iframeDoc) return;

          if (!iframeDoc.getElementById('section-toolbar-styles')) {
            const style = iframeDoc.createElement('style');
            style.id = 'section-toolbar-styles';
            style.innerHTML = `
              .section-toolbar {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                margin: 8px 0;
                background: transparent;
                border: none;
                cursor: pointer;
                font-size: 14px;
                position: absolute;
                top: 0px;
                left: 0px;
                z-index: 100;
              }
              .section-toolbar::before,
              .section-toolbar::after {
                content: "";
                flex: 1;
                border-bottom: 1px dashed currentColor;
                margin: 0 8px;
              }
              .section-toolbar.add { color: #3E5DE6; }
              .section-toolbar.remove { color: #ff0000; }

              .setupPdfPageBreakButton {
                border: 1px solid currentColor;
                border-radius: 4px;
                padding: 2px 8px;
                background: #fff;
              }

              section { 
                position: relative;
                zoom: ${SECTION_ZOOM_FACTOR};
                padding-left: ${SECTION_PADDING_FACTOR} !important;
                padding-right: ${SECTION_PADDING_FACTOR} !important;
              }

              header { zoom: ${HEADER_FOOTER_ZOOM_FACTOR}; }
              footer { zoom: ${HEADER_FOOTER_ZOOM_FACTOR}; }
              .powered-by { display: none; }
              @media print {
                .section-toolbar { display: none !important; }
                section.${pageBreakClassName} {
                  break-before: page;
                  page-break-before: always;
                }
              }
            `;
            iframeDoc.head.appendChild(style);
          }
          // set timeout to add section buttons after the iframe is loaded
          setTimeout(() => addSectionButtons(iframeDoc), 50);
        }}
      />
    </Paper>
  );
};
